import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { api } from "../../../../api/api";
import { AxiosResponse } from "axios";
import {
  Alert,
  Chip,
  DialogContentText,
  Stack,
  Typography,
} from "@mui/material";
import { ModalDialogConfirmStatus } from "components";
import { useTypedSelector } from "store";
import {
  ParentPropertyContextType,
  useParentPropertyContext,
} from "../../pages/ProductDetail/ProductLineProperties/ParentPropertyContext";
import { StandardSubproperties } from "store/reducers/products/subproperties/pl-subproperties.types";
import {
  DomainEnum,
  PropertyResponse,
} from "store/reducers/products/properties/pl-properties.types";
import {
  RuleTable,
  RuleTablePage,
  RuleTableResponse,
  TablesTypeEnum,
} from "types/table";
import { getSubPropertiesAction } from "store/reducers/products/subproperties/pl-subproperties.actions";
import { addTableToStoreAction } from "store/reducers/products/tables/tables.actions";
import { ProcessState } from "types/process";
import RulesTable from "../RulesTable/RulesTable.component";

export interface ValidSubpropMacroDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ValidSubpropMacroDialog: React.FC<
  ValidSubpropMacroDialogProps
> = ({ open, setOpen }) => {
  // State
  const [minSubprop, setMinSubprop] = useState<PropertyResponse | null>(null);
  const [maxSubprop, setMaxSubprop] = useState<PropertyResponse | null>(null);
  const [validSubprop, setValidSubprop] = useState<PropertyResponse | null>(
    null
  );
  const [hasValidTable, setHasValidTable] = useState<boolean>(false);
  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [statusMessage, setStatusMessage] = useState<React.ReactNode>("");
  const [newTableId, setNewTableId] = useState<string>("");

  // Redux Store
  const dispatch = useDispatch();
  const { subproperties } = useTypedSelector((state) => state.products);
  //Get the new table from redux store if it is there
  const tableData: RuleTableResponse | undefined = useTypedSelector((s) =>
    newTableId ? s.products.tables.byId[newTableId] : undefined
  );

  // context variables
  const parentPropertyContext: ParentPropertyContextType =
    useParentPropertyContext();

  useEffect(() => {
    // only check when dialog opens
    if (!open) return;

    //Load the component state from the parent propery and subproperties
    loadStateFromProperties();
  }, [open]);

  // state derived helper variables
  const disableCancelButton = processState !== ProcessState.IDLE;
  const disableCreateButton = processState !== ProcessState.IDLE;
  const isRangeProp = parentPropertyContext.parentDomain === DomainEnum.RANGE;
  const parentName: string =
    parentPropertyContext?.parentPropertyName ?? "(unknown)";
  const minSubpropName = `${parentName}${StandardSubproperties.MIN}`;
  const maxSubpropName = `${parentName}${StandardSubproperties.MAX}`;
  const validSubpropName = `${parentName}${StandardSubproperties.VALID}`;

  //Callbacks
  const handleCloseDialog = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // Private Methods
  const initializeState = () => {
    setMinSubprop(null);
    setMaxSubprop(null);
    setValidSubprop(null);
    setHasValidTable(false);
    setProcessState(ProcessState.IDLE);
    setStatusMessage("");
    setNewTableId("");
  };

  const loadStateFromProperties = async () => {
    // 1. Ensure state is properly initialized
    initializeState();

    // 2. See if .VALID subproperty already exists
    const validSubprop = findSubproperty(StandardSubproperties.VALID);
    setValidSubprop(validSubprop);

    // 3. If this is a Range Property see if .MIN and .MAX properties exist
    if (isRangeProp) {
      setMinSubprop(findSubproperty(StandardSubproperties.MIN));
      setMaxSubprop(findSubproperty(StandardSubproperties.MAX));
    }

    // 4. If we found a .VALID subprop, check if any LOOKUP tables use it
    if (validSubprop) {
      try {
        const validRulesetIds = (validSubprop.ruleSets ?? []).map(
          (rs) => rs.id
        );

        const isUsed = await hasLookupTablesUsingValidSubproperty(
          // use whatever your context exposes for product line id
          parentPropertyContext.productLineId,
          validSubprop.id ?? "",
          validRulesetIds
        );
        setHasValidTable(isUsed);
      } catch (err) {
        console.error("Error checking for LOOKUP tables that use .VALID:", err);
      }
    } else {
      setHasValidTable(false);
    }
  };

  const findSubproperty = (subpropName: string): PropertyResponse | null => {
    //Build the subprop name
    const fullSubpropName =
      parentPropertyContext.parentPropertyName + subpropName;

    //Find all the detail on the parent property's subproperties
    const mySubproperties =
      subproperties.byParentPropertyId[parentPropertyContext.parentId];
    if (!mySubproperties) return null;

    let targetSubProperty = null;
    for (const key in mySubproperties.standardById) {
      const subProperty = mySubproperties.standardById[key];
      if (subProperty.name === fullSubpropName) {
        targetSubProperty = subProperty;
        break;
      }
    }

    return targetSubProperty;
  };

  async function hasLookupTablesUsingValidSubproperty(
    productLineId: string,
    validSubpropertyId: string,
    validSubpropertyRulesetIds: string[]
  ): Promise<boolean> {
    try {
      // Retrieve all LOOKUP rule tables that reference this property
      const response: AxiosResponse<RuleTablePage> =
        await api.getRuleTablesLight(
          productLineId,
          0, // all tables
          0, // first page
          [TablesTypeEnum.LOOKUP],
          validSubpropertyRulesetIds, // rulesetIds
          [validSubpropertyId]
        );

      const ruleTables: RuleTable[] = response.data?.ruleTables ?? [];

      // Now do the stricter check: is this property a looked up property?
      for (const table of ruleTables) {
        // Make sure we have propertyIds and lookupFieldIndex
        const propertyIds = table.propertyIds ?? [];
        const lookUpFieldIndex = table.lookUpFieldIndex ?? -1;
        if (!Array.isArray(propertyIds) || lookUpFieldIndex < 0) {
          continue;
        }

        const index = propertyIds.indexOf(validSubpropertyId);
        if (index !== -1 && index <= lookUpFieldIndex) {
          // found a table where .VALID is a looked up property
          return true;
        }
      }

      // none matched the condition
      return false;
    } catch (error) {
      console.error(
        "Error while checking for LOOKUP tables using .VALID subproperty:",
        error
      );
      return false;
    }
  }

  const runMacro = useCallback(async () => {
    try {
      setProcessState(ProcessState.PROCESSING);
      setStatusMessage("");

      // If we don't already have a .VALID subproperty in state, create it
      if (!validSubprop) {
        setStatusMessage(`Creating ${validSubpropName} subproperty.`);
        const response = await api.createValidSubproperty(
          parentPropertyContext.productLineId,
          parentPropertyContext.parentId
        );

        // because _postRequest returns error responses too
        if (!response || response.status < 200 || response.status >= 300) {
          const msg = `Creating ${validSubpropName} subproperty failed.  
              ${response.statusText}`;
          setStatusMessage(msg);
          setProcessState(ProcessState.ERROR);
          console.error(msg, response?.status, response?.data);
          return;
        }

        const created = response.data as PropertyResponse;
        if (!created?.id) {
          const msg = `Creating ${validSubpropName} subproperty failed.  createValidSubproperty returned invalid payload"
            ${response.statusText}`;
          setStatusMessage(msg);
          setProcessState(ProcessState.ERROR);
          console.error(msg, response?.status, response?.data);
          return;
        }

        //Intentionally not saving the newly created .VALID subproperty to
        //state so that the content section of the dialog does not update.
        //setValidSubprop(created);
        setStatusMessage(`Created ${validSubpropName} subproperty.`);
      }

      // 2) create the standard .VALID LOOKUP table
      setStatusMessage(`Creating ${validSubpropName} LOOKUP table.`);
      const tableResponse = await api.createStandardVALIDLookupTable(
        parentPropertyContext.productLineId,
        parentPropertyContext.parentId,
        true
      );

      if (
        !tableResponse ||
        tableResponse.status < 200 ||
        tableResponse.status >= 300
      ) {
        const msg = `Creating ${validSubpropName} LOOKUP table failed.  
              ${tableResponse.statusText}`;
        setStatusMessage(msg);
        setProcessState(ProcessState.ERROR);
        console.error(msg, tableResponse?.status, tableResponse?.data);
        return;
      }

      //Get the new table and add to state
      setStatusMessage("Loading the new table");
      const createdTable = tableResponse.data as RuleTableResponse;
      dispatch(addTableToStoreAction({ table: createdTable }));
      setNewTableId(createdTable.id);

      //Refresh all subproperties of the parent to catch any that might
      //have been created.
      setStatusMessage("Refreshing all subproperties.");
      dispatch(
        getSubPropertiesAction({
          productLineId: parentPropertyContext.productLineId,
          PLPropertyId: parentPropertyContext.parentId,
        })
      );

      setStatusMessage(
        <Typography>
          New <strong>{validSubpropName}</strong> LOOKUP table is shown below.
        </Typography>
      );
      setProcessState(ProcessState.COMPLETE);
    } catch (err: any) {
      const msg = `Creating ${validSubpropName} LOOKUP table failed.`;
      setStatusMessage(msg);
      setProcessState(ProcessState.ERROR);
      console.error(msg, err);
    }
  }, [
    validSubprop,
    validSubpropName,
    parentPropertyContext.productLineId,
    parentPropertyContext.parentId,
  ]);

  // Build the content section for the dialog
  const buildContent = useMemo<React.ReactNode>(() => {
    const rulesets =
      validSubprop?.ruleSets ??
      parentPropertyContext?.parentPropertyRulesets ??
      [];

    return (
      <ContentStack>
        <DialogContentText>
          Clicking on the <strong>Create</strong> button below will do the
          following:
        </DialogContentText>

        <BulletList>
          {!validSubprop && (
            <BulletItem>
              Create subproperty <strong>{validSubpropName}</strong>.
            </BulletItem>
          )}

          {isRangeProp && !minSubprop && (
            <BulletItem>
              Create subproperty <strong>{minSubpropName}</strong>.
            </BulletItem>
          )}

          {isRangeProp && !maxSubprop && (
            <BulletItem>
              Create subproperty <strong>{maxSubpropName}</strong>.
            </BulletItem>
          )}

          {hasValidTable && (
            <BulletItem>
              Delete all existing <strong>{validSubpropName}</strong> LOOKUP
              tables.
            </BulletItem>
          )}

          <BulletItem>
            Create a new <strong>{validSubpropName}</strong> LOOKUP table to
            determine if <strong>{parentName}</strong>{" "}
            {isRangeProp ? "is within range." : "has been assigned."}
          </BulletItem>

          {rulesets.length > 0 && (
            <BulletItem>
              The new LOOKUP table will have these rulesets:
              <RulesetChips direction="row" flexWrap="wrap" gap={1}>
                {rulesets.map((r) => (
                  <Chip
                    key={r.id ?? r.name}
                    label={r.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </RulesetChips>
            </BulletItem>
          )}
        </BulletList>

        {hasValidTable && (
          <Alert variant="outlined" severity="warning" sx={{ mt: 1 }}>
            This will replace <strong>ALL</strong> existing LOOKUP tables for{" "}
            <strong>{validSubprop?.name}</strong>. This cannot be undone.
          </Alert>
        )}
      </ContentStack>
    );
  }, [
    validSubprop,
    minSubprop,
    maxSubprop,
    isRangeProp,
    hasValidTable,
    parentPropertyContext,
  ]);

  // at the bottom of the component, before return()
  const buildResult = useMemo<React.ReactNode>(() => {
    // When complete AND we have a new table, show the table
    if (processState === ProcessState.COMPLETE) {
      if (!tableData)
        return (
          <Alert variant="outlined" severity="error">
            <strong>Table not found</strong>
          </Alert>
        );

      return (
        <ResultWrapper>
          <RulesTable
            id={tableData.id}
            ruleTableData={tableData}
            hideCommentsAndSources={false}
          />
        </ResultWrapper>
      );
    }

    return null;
  }, [processState, tableData]);

  return (
    <ModalDialogConfirmStatus
      //Dialog props
      open={open}
      fullWidth={false}
      maxWidth={false}
      title={`Add ${validSubpropName} Subproperty and standard LOOKUP Table`}
      onClose={handleCloseDialog}
      disableEscapeKeyDown={true}
      disableCloseOnBackdropClick={true}
      //Content props
      contentSection={buildContent}
      hideContentOnProcessComplete={true}
      //Actions props
      defaultButton="OK"
      cancelButtonText="Cancel"
      cancelButtonDisabled={disableCancelButton}
      onCancelButtonClick={handleCloseDialog}
      okButtonText="Create"
      okButtonDisabled={disableCreateButton}
      onOKButtonClick={runMacro}
      //Status props
      processState={processState}
      statusMessage={statusMessage}
      resultSection={buildResult}
    />
  );
};

const ContentStack = styled(Stack)`
  gap: 16px;
`;

const BulletList = styled.ul`
  list-style-type: disc;
  list-style-position: outside;
  padding-left: 1.5rem;
  margin: 0;
`;

const BulletItem = styled.li`
  margin: 8px 0;
  font-size: 1rem;
  line-height: 1.5;
`;

const RulesetChips = styled(Stack)`
  margin-top: 0.5rem;
  margin-left: 0rem;
`;

const ResultWrapper = styled.div`
  padding-left: 30px; /* move the whole table to the right a bit */
`;

export default ValidSubpropMacroDialog;
